<template>
  <div class="mayday-diagnostic-editor">
    <!-- EDITOR SECTION -->
    <div class="row editor_row">
      <div
        class="col-12 text-edition"
        :class="{ 'image-pointer': !editable }"
        @mouseleave="onMouseLeave"
      >
        <mayday-editor
          v-if="showEditor"
          ref="diagnostic-editor"
          v-model="content"
          :editable="editable"
          :options="options"
          :generate-grammar="fetchCorrectionBuffer"
          :generate-style="fetchStyleBuffer"
          :subdomain="subdomain"
          :isParametic="isParametric"
          :generate-translation="fetchTranslationBuffer"
          @update="onUpdate"
          @open-link-modal="openLinkModal"
          @click:inline-content="handleOnClickInlineContent"
          @click:attached-file="handleClickAttachedFile"
          @click:image="handleOnImageClick($event.src)"
          @click:copy="handleOnCopy"
          @click:open-link="handleOpenLink"
        />
      </div>
      <LightBox v-show="!!imageUrl" :image-url.sync="imageUrl" />
      <MaydayEditorOpenLinkModal
        v-if="linkModal.display"
        :display="linkModal.display"
        :src="linkModal.src"
        @close="closeLinkModal"
      />
    </div>
    <div class="row">
      <div class="col-10"></div>
      <div class="col-2"></div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import MaydayEditorOpenLinkModal from './utils/MaydayEditorOpenLinkModal';
import LightBox from '@/components/LightBox';

export default {
  name: 'MaydayDiagnosticEditor',
  components: { MaydayEditorOpenLinkModal, LightBox },
  props: {
    contentId: String,
    body: String,
    editable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      content: '',
      linkModal: {
        display: false,
        src: '',
      },
      imageUrl: '',
      showEditor: true,
      options: {
        fontOptions: true,
        extraFontOptions: true,
        colorOptions: true,
        structurationOptions: true,
        extraStructurationOptions: true,
        tableOptions: true,
        richOptions: true,
        tocClickOptions: false,
        allowToc: true,
        allowSummary: false,
        allowActionItems: false,
        allowTranslation: false,
        allowGrammarCorrection: false,
        allowToneImprovement: false,
      },
    };
  },
  created() {
    this.setEditorContent(this.body);
    setTimeout(() => {
      const hash = this.$route.hash;
      this.handleOnFocusHash(hash);
    }, 1000);
    this.options.allowTranslation = this.companyAllowTranslationPreference;
    this.options.allowGrammarCorrection =
      this.companyAllowGrammarCorrectionPreference;
    this.options.allowToneImprovement =
      this.companyAllowToneImprovementPreference;
    this.options.allowActionItems = this.companyAllowActionItemsPreference;
  },
  mounted() {
    this.$root.$on('content-restored', () => {
      this.setEditorContent(this.body);
    });
  },
  computed: {
    subdomain() {
      return window.location.origin;
    },
    ...mapGetters([
      'companyAllowSummaryPreference',
      'companyAllowTranslationPreference',
      'companyAllowGrammarCorrectionPreference',
      'companyAllowToneImprovementPreference',
      'companyAllowActionItemsPreference',
      'isParametric',
    ]),
  },
  methods: {
    onUpdate(body) {
      this.$emit('update-written-content', { body });
    },
    setEditorContent(rawContent) {
      this.showEditor = false;
      this.$nextTick(() => {
        this.showEditor = true;
        const maydayEditor = this.$refs['diagnostic-editor'];
        this.content = rawContent;
        if (maydayEditor) {
          maydayEditor.clearHistory();
          maydayEditor.editor.commands.setContent(this.content);
        }
      });
    },
    focusEditor() {
      const maydayEditor = this.$refs['diagnostic-editor'];
      if (maydayEditor) {
        maydayEditor.editor.commands.focus();
      }
    },
    openLinkModal(src) {
      this.linkModal.display = true;
      this.linkModal.src = src;
    },
    closeLinkModal() {
      this.linkModal.display = false;
      this.linkModal.src = '';
    },
    handleClickAttachedFile(evt) {
      if (evt && evt.href) {
        window.open(evt.href, '_blank');
      }
    },
    onMouseLeave({ relatedTarget }) {
      if (!relatedTarget) return;
      if (!relatedTarget.className) return;
      const { className } = relatedTarget;
      const handlerClass = 'mayday-editor__drag-icon-container';

      if (className === handlerClass) return;

      const editorDragHandleElement =
        document.getElementsByClassName(handlerClass);

      if (!editorDragHandleElement || !editorDragHandleElement[0]) return;

      editorDragHandleElement[0].style.visibility = 'hidden';
    },
    handleOnImageClick(src) {
      if (!this.editable && src) {
        this.imageUrl = src;
      }
    },
    handleOnFocusHash(hash) {
      if (!hash) return;
      const id = hash.slice(1);

      const uniqueNode = document.querySelector(
        `[data-uid="${id}"], [id="${id}"]`,
      );
      if (!uniqueNode) return;
      uniqueNode.scrollIntoView({ behavior: 'smooth' });
    },
    handleOnCopy(uniqueId) {
      const url = new URL(window.location.href);
      navigator.clipboard.writeText(`${url.origin}${url.pathname}#${uniqueId}`);

      this.$message(this.$t('editor.copy'));
    },
    handleOpenLink({ href }) {
      window.open(href, '_blank');
    },
    async *fetchTranslationBuffer(text, language, abortController) {
      for await (const chunk of await this.fetchTranslation({
        text,
        language,
        abortController,
      })) {
        yield chunk;
      }
    },
    async *fetchCorrectionBuffer(text, _, abortController) {
      for await (const chunk of await this.fetchCorrection({
        text,
        abortController,
        correctionType: 'grammar',
      })) {
        yield chunk;
      }
    },
    async *fetchStyleBuffer(text, _, abortController) {
      for await (const chunk of await this.fetchCorrection({
        text,
        abortController,
        correctionType: 'style',
      })) {
        yield chunk;
      }
    },
    ...mapActions('knowledgeModule', ['handleOnClickInlineContent']),
    ...mapActions('brainModule', ['fetchTranslation', 'fetchCorrection']),
  },
  watch: {
    editable() {
      this.editor.setOptions({ editable: this.editable });
    },
    contentId: {
      handler(val, oldVal) {
        if (oldVal !== val) {
          this.setEditorContent(this.body);
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.editor_row {
  margin-right: 0px !important;
  margin-left: 0px !important;
}

/* TEXT EDITION */

.text-edition {
  background-color: transparent;
}

:deep() .mayday-editor {
  padding: 0;
}

:deep().mayday-editor__slash-menu-command-button {
  z-index: 1;
}

.image-pointer {
  :deep() .mayday-editor {
    &__image-wrapper {
      img {
        cursor: pointer;
      }
    }
  }
}

:deep() summary {
  color: black;
}
</style>
